import React, { useEffect, useMemo, useState } from "react";
import {
  makeStyles,
  Typography,
  Paper,
  Grid,
  Box,
  Backdrop,
  CircularProgress,
  Fade,
  Grow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
} from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";

import LocalGasStationIcon from "@material-ui/icons/LocalGasStation";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import { useDispatch, useSelector } from "react-redux";
import { getGasBottleSigns } from "../../actions/inventoryAction";
import { getLocations } from "../../actions/usersAction";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #1f2128 0%, #2d3342 100%)"
        : "linear-gradient(135deg, #eef2f3 0%, #d9e2ec 100%)",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    background: "linear-gradient(135deg, #1d976c 0%, #93f9b9 100%)",
    color: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
  },
  title: {
    fontWeight: 900,
    fontSize: 24,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    opacity: 0.95,
    fontSize: 18,
  },
  headerDateTime: {
    textAlign: "right",
  },
  dateText: {
    fontSize: 22,
    fontWeight: 700,
  },
  timeText: {
    fontSize: 18,
    fontWeight: 600,
  },
  statsContainer: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(1),
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
  },
  statsGrid: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  statItem: {
    textAlign: "center",
    flex: 1,
    position: "relative",
    "&:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      right: -theme.spacing(1.5),
      top: "50%",
      transform: "translateY(-50%)",
      height: "60%",
      width: 2,
      backgroundColor: theme.palette.divider,
      opacity: 1,
    },
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 900,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  searchRow: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
    borderRadius: 12,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    background: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
  },
  table: {
    minWidth: 760,
  },
  tableHead: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #434343 0%, #000000 100%)"
        : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  tableHeadCell: {
    color: "#fff !important",
    fontWeight: 900,
    fontSize: 12,
    textTransform: "uppercase",
    padding: "12px 16px",
  },
  tableCell: {
    padding: "10px 16px",
    fontSize: 13,
  },
  serialCell: {
    fontWeight: 800,
    letterSpacing: 0.4,
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableRowSignedOut: {
    borderLeft: "4px solid #ef5350",
    backgroundColor: "rgba(239, 83, 80, 0.06)",
  },
  tableRowSignedIn: {
    borderLeft: "4px solid #66bb6a",
    backgroundColor: "rgba(102, 187, 106, 0.06)",
  },
  statusChip: {
    fontWeight: 700,
    fontSize: 11,
    height: 28,
  },
  statusSignedOut: {
    backgroundColor: "#ef5350",
    color: "#fff",
  },
  statusSignedIn: {
    backgroundColor: "#66bb6a",
    color: "#fff",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 72,
    marginBottom: theme.spacing(2),
    opacity: 0.35,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const CompressedGasBottleDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");

  const { loading, gasBottleSigns } = useSelector((state) => state.inventory);
  const { profile, locations } = useSelector((state) => state.users);

  const isInventoryAdmin = profile?.role?.some((role) =>
    ["admin", "inventory_admin"].includes(role.name)
  );
  const hasAssignedLocation = Boolean(profile?.location?._id || profile?.location?.id || profile?.location);
  const canFilterByLocation = isInventoryAdmin && !hasAssignedLocation;

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    if (canFilterByLocation) {
      dispatch(getLocations());
    }
  }, [dispatch, canFilterByLocation]);

  useEffect(() => {
    dispatch(getGasBottleSigns(selectedLocation || undefined));
  }, [dispatch, selectedLocation]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(getGasBottleSigns(selectedLocation || undefined));
    }, 300000);

    return () => clearInterval(interval);
  }, [dispatch, selectedLocation]);

  const latestBySerial = useMemo(() => {
    const uniqueMap = new Map();
    (gasBottleSigns || []).forEach((record) => {
      const serial = record?.serialNumber;
      if (!serial || uniqueMap.has(serial)) {
        return;
      }
      uniqueMap.set(serial, record);
    });
    return Array.from(uniqueMap.values());
  }, [gasBottleSigns]);

  const signedOutBottles = useMemo(
    () => latestBySerial.filter((row) => row.signType === "Sign Out"),
    [latestBySerial]
  );

  const filteredBottles = useMemo(() => {
    const value = search.toLowerCase().trim();
    if (!value) {
      return signedOutBottles;
    }

    return signedOutBottles.filter((row) => {
      return (
        row.serialNumber?.toLowerCase().includes(value) ||
        row.gasType?.toLowerCase().includes(value) ||
        row.jobNumber?.toLowerCase().includes(value) ||
        row.createdBy?.displayname?.toLowerCase().includes(value) ||
        row.location?.displayname?.toLowerCase().includes(value) ||
        row.location?.name?.toLowerCase().includes(value)
      );
    });
  }, [signedOutBottles, search]);

  const totalCount = filteredBottles.length;
  const signedOutCount = filteredBottles.length;

  const getStatusClass = (signType) =>
    signType === "Sign Out" ? classes.statusSignedOut : classes.statusSignedIn;

  const getStatusIcon = (signType) =>
    signType === "Sign Out" ? <WarningIcon style={{ fontSize: 16 }} /> : <CheckCircleIcon style={{ fontSize: 16 }} />;

  const getRowClass = (signType) =>
    signType === "Sign Out"
      ? clsx(classes.tableRow, classes.tableRowSignedOut)
      : clsx(classes.tableRow, classes.tableRowSignedIn);

  return (
    <div className={classes.root}>
      <Fade in={visible} timeout={800}>
        <Paper className={classes.header}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography className={classes.title}>Compressed Gas Dashboard</Typography>
              <Typography className={classes.subtitle}>
                {profile?.location?.displayname || "All Locations"} - Bottles Currently Signed Out
              </Typography>
            </Grid>
            <Grid item className={classes.headerDateTime}>
              <Typography className={classes.dateText}>{moment().format("ddd, MMM DD, YYYY")}</Typography>
              <Typography className={classes.timeText}>{moment().format("h:mm A")}</Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} className={classes.statsContainer}>
            <Grid item xs={12}>
              <Grow in={visible} timeout={600}>
                <Paper className={classes.statCard}>
                  <Box className={classes.statsGrid}>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statLabel}>Bottles Currently Out</Typography>
                      <Typography className={classes.statNumber}>{totalCount}</Typography>
                    </Box>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statLabel}>Signed Out</Typography>
                      <Typography className={classes.statNumber} style={{ color: "#ef5350" }}>
                        {signedOutCount}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grow>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      <Grid container spacing={2} alignItems="center" className={classes.searchRow}>
        <Grid item xs={12} md={canFilterByLocation ? 8 : 12}>
          <Grow in={visible} timeout={800}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search signed-out bottles by serial, gas type, operator, or location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <Chip
                      label={`${filteredBottles.length} ${filteredBottles.length === 1 ? "result" : "results"}`}
                      size="small"
                      color="primary"
                      style={{ marginRight: 8 }}
                    />
                    <ClearIcon
                      style={{ cursor: "pointer", color: "#999" }}
                      onClick={() => setSearch("")}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grow>
        </Grid>

        {canFilterByLocation && (
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Filter Location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className={classes.searchField}
            >
              <MenuItem value="">All Locations</MenuItem>
              {(locations || []).map((location) => (
                <MenuItem key={location._id} value={location._id}>
                  {location.displayname || location.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      <Grow in={visible} timeout={1000}>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Serial Number</TableCell>
                <TableCell className={classes.tableHeadCell}>Gas Type</TableCell>
                <TableCell className={classes.tableHeadCell}>Current State</TableCell>
                <TableCell className={classes.tableHeadCell}>Last Action</TableCell>
                <TableCell className={classes.tableHeadCell}>Job #</TableCell>
                <TableCell className={classes.tableHeadCell}>Operator</TableCell>
                <TableCell className={classes.tableHeadCell}>Location</TableCell>
                <TableCell className={classes.tableHeadCell}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBottles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: 40 }}>
                    <Box className={classes.emptyState}>
                      <LocalGasStationIcon className={classes.emptyIcon} />
                      <Typography variant="h6">{search ? "No bottles found" : "No bottle records found"}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {search
                          ? `No signed-out bottles match "${search}"`
                          : "No compressed gas bottles are currently signed out"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBottles.map((row) => (
                  <TableRow key={row._id} className={getRowClass(row.signType)}>
                    <TableCell className={clsx(classes.tableCell, classes.serialCell)}>{row.serialNumber}</TableCell>
                    <TableCell className={classes.tableCell}>{row.gasType || "-"}</TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={row.signType === "Sign Out" ? "Signed Out" : "Signed In"}
                        icon={getStatusIcon(row.signType)}
                        className={clsx(classes.statusChip, getStatusClass(row.signType))}
                        size="small"
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>{row.signType}</TableCell>
                    <TableCell className={classes.tableCell}>{row.jobNumber || "N/A"}</TableCell>
                    <TableCell className={classes.tableCell}>{row.createdBy?.displayname || "N/A"}</TableCell>
                    <TableCell className={classes.tableCell}>{row.location?.displayname || row.location?.name || "N/A"}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {row.signedAt ? moment(row.signedAt).format("MMM DD, YYYY h:mm A") : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grow>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default CompressedGasBottleDashboard;
